import React, { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, XCircle, Receipt, Check, X } from 'lucide-react';
import { apiError, fmtClock, fmtDate, fmtDateTime, portalApi } from './portalApi';
import { Pill } from './portalUi';

export const SR_STATUS = {
  new: ['new', 'Sent — waiting for 365 to review'],
  acknowledged: ['acknowledged', 'Seen by 365 — being reviewed'],
  approved: ['pending', 'Approved by your PM — dispatch is booking the crew'],
  scheduled: ['scheduled', 'Crew scheduled'],
  closed: ['closed', 'Closed'],
  cancelled: ['cancelled', 'Cancelled by you'],
};
const EDITABLE = ['new', 'acknowledged'];
const CANCELLABLE = ['new', 'acknowledged', 'approved', 'scheduled'];
const PO_OPEN = ['new', 'acknowledged', 'approved', 'scheduled'];

const PoEditor = ({ r, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [po, setPo] = useState(r.po_number || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const res = await portalApi.put(`/service-requests/${r.id}/po`, { po_number: po }); toast.success(po ? `PO# ${po} sent to 365` : 'PO# removed'); onSaved(res.data); setEditing(false); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`sr-po-${r.request_number}`}>
        <Receipt className="w-3.5 h-3.5 text-slate-400" />
        {r.po_number ? <span className="text-slate-700">PO# <b className="p-mono text-slate-900">{r.po_number}</b></span> : <span className="text-slate-500">No PO# yet</span>}
        {PO_OPEN.includes(r.status) && <button type="button" className="text-blue-700 font-semibold hover:underline" onClick={() => { setPo(r.po_number || ''); setEditing(true); }} data-testid={`sr-po-edit-${r.request_number}`}>{r.po_number ? 'Change' : 'Add PO#'}</button>}
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-1.5" data-testid={`sr-po-form-${r.request_number}`}>
      <input className="p-input !py-1 !text-sm !w-44" value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO number" maxLength={60} autoFocus data-testid={`sr-po-input-${r.request_number}`} />
      <button type="button" className="p-btn p-btn-primary !py-1 !px-2.5 text-xs" disabled={busy} onClick={save} data-testid={`sr-po-save-${r.request_number}`}><Check className="w-3.5 h-3.5" /> Save</button>
      <button type="button" className="p-btn p-btn-ghost !py-1 !px-2 text-xs" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

const CancelBox = ({ r, onDone, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const res = await portalApi.post(`/service-requests/${r.id}/cancel`, { reason }); toast.success(`Request ${r.request_number} cancelled — 365 has been notified`); onDone(res.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid={`sr-cancel-box-${r.request_number}`}>
      <div className="text-sm font-semibold text-rose-800">Cancel this request?</div>
      {r.status === 'scheduled' && <p className="text-xs text-rose-700 mt-0.5">Crew is already booked — dispatch will be alerted right away so they can pull the job.</p>}
      <textarea className="p-input mt-2 !text-sm" rows={2} placeholder="Reason (optional) — e.g. issue resolved, rescheduling to next month" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`sr-cancel-reason-${r.request_number}`} />
      <div className="flex justify-end gap-2 mt-2">
        <button type="button" className="p-btn p-btn-ghost !py-1 !px-3 text-xs" onClick={onClose} data-testid={`sr-cancel-no-${r.request_number}`}>Keep it</button>
        <button type="button" className="p-btn !py-1 !px-3 text-xs bg-rose-600 text-white border-rose-600 hover:bg-rose-700" disabled={busy} onClick={go} data-testid={`sr-cancel-yes-${r.request_number}`}>{busy ? 'Cancelling…' : 'Yes, cancel request'}</button>
      </div>
    </div>
  );
};

export const RequestRow = ({ r, catLabel, editing, onEdit, onChanged }) => {
  const [cancelling, setCancelling] = useState(false);
  const [tone, label] = SR_STATUS[r.status] || ['info', r.status];
  return (
    <div className={`rounded-xl border p-3.5 ${editing ? 'border-blue-400 ring-2 ring-blue-100' : r.status === 'cancelled' ? 'border-slate-200 opacity-70' : 'border-slate-200'}`} data-testid={`sr-row-${r.request_number}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="p-mono font-bold text-slate-900">{r.request_number}</span>
        <div className="flex gap-1.5">{r.urgency !== 'normal' && r.status !== 'cancelled' && <Pill tone={r.urgency === 'critical' ? 'expired' : 'expiring'}>{r.urgency}</Pill>}<Pill tone={tone} testId={`sr-status-${r.request_number}`}>{r.status}</Pill></div>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-1">{catLabel[r.category] || r.category}{r.equipment ? ` — ${r.equipment}` : ''}</div>
      <div className="text-xs text-slate-500 mt-0.5">{r.site || 'site not given'}{r.start_date ? ` · wanted ${fmtDate(r.start_date)}${r.start_time ? ` ${fmtClock(r.start_time)}` : ''}` : ' · ASAP'}{r.headcount ? ` · ${r.headcount} crew` : ''}</div>
      <p className="text-sm text-slate-600 mt-1.5 line-clamp-3">{r.description}</p>
      {r.status === 'scheduled' && r.scheduled_start && <div className="mt-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900" data-testid={`sr-scheduled-${r.request_number}`}><b>Scheduled:</b> {r.scheduled_crew || ''} crew · {fmtDate(r.scheduled_start)}{r.scheduled_end && r.scheduled_end !== r.scheduled_start ? ` – ${fmtDate(r.scheduled_end)}` : ''}</div>}
      {r.staff_note && <div className="mt-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-blue-900"><b>365:</b> {r.staff_note}</div>}
      {r.status === 'cancelled' && r.cancel_reason && <div className="mt-2 text-xs text-slate-500 italic">Reason: {r.cancel_reason}</div>}
      {r.linked_wo && <div className="mt-1.5 text-xs text-slate-600">Work order <span className="p-mono font-bold text-blue-700">{r.linked_wo}</span></div>}
      <PoEditor r={r} onSaved={onChanged} />
      {cancelling ? <CancelBox r={r} onClose={() => setCancelling(false)} onDone={(u) => { setCancelling(false); onChanged(u); }} /> : (
        <div className="flex items-center justify-between gap-2 mt-2.5">
          <div className="text-[11px] text-slate-400 p-mono">Submitted {fmtDateTime(r.created_at)}</div>
          <div className="flex gap-1.5">
            {EDITABLE.includes(r.status) && <button type="button" className="p-btn p-btn-ghost !py-1 !px-2.5 text-xs" onClick={() => onEdit(r)} data-testid={`sr-edit-${r.request_number}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>}
            {CANCELLABLE.includes(r.status) && <button type="button" className="p-btn p-btn-ghost !py-1 !px-2.5 text-xs text-rose-700" onClick={() => setCancelling(true)} data-testid={`sr-cancel-${r.request_number}`}><XCircle className="w-3.5 h-3.5" /> Cancel</button>}
          </div>
        </div>)}
    </div>
  );
};
